import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import _ from 'lodash'
import axios from 'axios'
import { getAxiosDefaultConfig } from 'utils'
import { useLocation } from 'react-router-dom'
import {
  ChannelAdvisorFragmentHandler,
  handleChannelAdvisorErrors,
  handleChannelAdvisorSuccess
} from './channels/channeladvisor'
import { handleTikTokErrors, handleTikTokSuccess } from './channels/tiktok/oauthStatus'
import { handleLinkedInErrors, handleLinkedInSuccess } from './channels/linkedin/oauthStatus'
import { toast } from 'react-toastify'
import Search from './components/Search'
import IntegrationCard from './components/IntegrationCard'
import PayhelmAPIKeyCard from './components/PayhelmAPIKeyCard'
import PayHelmMcpCustomToolsCard from './components/PayHelmMcpCustomToolsCard'
import AlexaSkillCard from './components/AlexaSkillCard'
import CustomToolsIntegrationsModal from './components/CustomToolsIntegrationsModal'
import {
  useIntegrations,
  useCategories,
  payhelmApiKeyCardSearchRecord,
  payhelmMcpCustomToolsCardSearchRecord,
  alexaSkillCardSearchRecord,
} from './hooks'
import BackfillModal from './components/BackfillModal'
import ImportOrdersModal from './components/ImportOrdersModal'
import UploadModal from 'sharedComponents/UploadCSVButton/UploadModal'
import InkfrogListingsUploadModal from './components/InkfrogListingsUploadModal'
import InkfrogOrdersUploadModal from './components/InkfrogOrdersUploadModal'
import { useDispatch, useSelector } from 'react-redux'
import { selectRootAccounts, selectRootUser, selectRootAdmin, selectRootAccessPermission } from '../../rootSlice'
import { canManageAllTeamIntegrations } from '../Settings/views/SharedAccounts/teamPagePermissions'
import { isTeamOwner } from './isTeamOwner'
import {
  closeBackfillModal,
  closeConnectionModal,
  openConnectionModal,
  setCurrentChannel,
  closeCsvModal,
  closeInkfrogCsvModal,
  closeImportOrdersModal,
  closeInkfrogOrdersCsvModal,
  closeSettingsModal,
  closeCustomToolsModal,
  fetchAllPublicKeys,
  selectBackillModalVisible,
  selectConnectionModalVisible,
  selectCsvModalVisible,
  selectInkfrogCsvModalVisible,
  selectImportOrdersModalVisible,
  selectInkfrogOrdersCsvModalVisible,
  selectSettingsModalVisible,
  selectCustomToolsModalVisible,
  selectAllPublicKeys,
  selectAllPublicKeysStatus,
  selectMcpPublicKeysStatus,
  selectCurrentChannel,
} from './integrationsSlice'
import SettingsModal from './components/SettingsModal'
import ConnectionModal from './components/ConnectionModal'
import CategoryPills from './components/CategoryPills'
import { fetchTeamAccounts, selectTeamAccounts, selectTeamAccountsStatus } from '../Settings/settingsSlice'
import { setAccounts } from 'actions/rootActions'
import { resolveIntegrationsOwnerId } from '../../modules/utils'
import { verifyLogin } from 'urls'
import { baseUrl } from '../../modules/urls'
import { Button, Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { changeApplication } from '../../actions/rootActions'
import { fetchCustomTools, selectCustomTools } from '../../features/openai/openaiSlice'
import {
  selectIsWorkspaceContext,
  selectOrganizations,
} from '../../features/organizations/organizationsSlice'
import {
  GMC_OAUTH_SETUP_PENDING_KEY,
  GMC_OAUTH_TOAST_DONE_KEY,
  dismissGmcOAuthSetupModal,
  isGmcAccountSetupDismissed,
  isGmcOAuthSetupDismissed,
} from './channels/googleMerchantCenter/gmcOAuthSession'
import {
  collectGmcRows,
  fetchGmcAccountsForConnection,
  saveMerchantCenterAccountId,
} from './channels/googleMerchantCenter/gmcAccountSetup'
import AiSubaccountSetupModal, {
  type SubaccountSetupEntry,
} from './components/AiSubaccountSetupModal'
import { getCookie, setCookie } from '../../modules/cookie'

function parseSubaccountOAuthReturn(
  search: string
): { platform: string; connectionId: string; saved?: string[] } | null {
  const params = new URLSearchParams(search)
  if (params.get('ga_oauth') === 'success') {
    const connectionId = params.get('ga_connection_id')
    if (!connectionId) return null
    return { platform: 'google_analytics', connectionId }
  }
  if (params.get('gtm_oauth') === 'success') {
    const connectionId = params.get('gtm_connection_id')
    if (!connectionId) return null
    return { platform: 'google_tag_manager', connectionId }
  }
  if (params.get('gads_oauth') === 'success') {
    const connectionId = params.get('gads_connection_id')
    if (!connectionId) return null
    return { platform: 'google_ads', connectionId }
  }
  if (params.get('gsc_oauth') === 'success') {
    const connectionId = params.get('gsc_connection_id')
    if (!connectionId) return null
    return { platform: 'google_search_console', connectionId }
  }
  if (params.get('bigquery_oauth') === 'success') {
    const connectionId = params.get('bigquery_connection_id')
    if (!connectionId) return null
    return { platform: 'bigquery', connectionId }
  }
  if (params.get('google_marketing_oauth') === 'success') {
    const connectionId = params.get('google_marketing_connection_id')
    const savedRaw = params.get('saved')
    if (!connectionId || !savedRaw) return null
    return { platform: 'google_marketing', connectionId, saved: savedRaw.split(',').filter(Boolean) }
  }
  return null
}

const GOOGLE_OAUTH_STATUS_PARAMS = [
  'ga_oauth',
  'gtm_oauth',
  'gsc_oauth',
  'gmc_oauth',
  'google_marketing_oauth',
] as const

function parseGoogleOAuthScopeContamination(search: string): string | null {
  const params = new URLSearchParams(search)
  return GOOGLE_OAUTH_STATUS_PARAMS.find((key) => params.get(key) === 'scope_contaminated') || null
}

/** Cookie backup when Ads OAuth runs in a popup and the parent only refreshes (no query params). */
function consumeGoogleAdsOAuthSetupCookie(): { platform: string; connectionId: string } | null {
  const connectionId = getCookie('gads_oauth_setup')
  if (!connectionId || typeof connectionId !== 'string') return null
  setCookie('gads_oauth_setup', '', 0)
  return { platform: 'google_ads', connectionId }
}

// Client-side mirror of papaya's `PLATFORMS_WITH_SUBACCOUNTS` (myAi.ts) — which of Google
// Marketing's granted products can go through the sub-account setup modal. `google_ads` never
// appears in Marketing's `saved` list (Marketing doesn't request Ads scopes) and
// `google_merchant_center` has its own dedicated setup flow (see the GMC OAuth-return effect
// above), so neither needs an entry here.
const MARKETING_SUBACCOUNT_PLATFORMS = new Set(['google_analytics', 'google_tag_manager', 'google_search_console', 'bigquery'])

function Integrations() {
  const dispatch = useDispatch()
  const location = useLocation()
  const customTools = useSelector(selectCustomTools)
  const customToolsModalVisible = useSelector(selectCustomToolsModalVisible)

  // Check if this is the ChannelAdvisor OAuth redirect page
  const isChannelAdvisorAccept = location.pathname === '/integrations/channeladvisor/accept'

  useEffect(() => {
    if (!isChannelAdvisorAccept) {
      dispatch(fetchCustomTools() as any)
    }
  }, [dispatch, isChannelAdvisorAccept])

  // If it's the ChannelAdvisor accept page, only render the fragment handler
  if (isChannelAdvisorAccept) {
    return <ChannelAdvisorFragmentHandler />
  }

  /**
   *
   * Handle accounts
   *
   */
  const user = useSelector(selectRootUser)
  const isAdmin = useSelector(selectRootAdmin)
  const rawAccessPermission = useSelector(selectRootAccessPermission)

  const canDisconnectAllTeamIntegrations = useMemo(() => {
    if (isTeamOwner(user) || isAdmin) return true
    return canManageAllTeamIntegrations(rawAccessPermission)
  }, [user, isAdmin, rawAccessPermission])
  const rootAccounts = useSelector(selectRootAccounts)
  const accounts = rootAccounts.map((_account) => ({ ..._account, isOwner: user.email === _account.owner }))
  const retrieveAccounts = useCallback(async () => {
    console.log('Retrieving accounts...')
    const loginRes = await axios.get(verifyLogin, getAxiosDefaultConfig())
    const { accounts: _accounts } = loginRes.data
    dispatch(setAccounts(_accounts))
  }, [dispatch])

  const isWorkspaceContext = useSelector(selectIsWorkspaceContext)
  const organizations = useSelector(selectOrganizations)
  const activeOrg = organizations?.find((org: any) => org.is_active)
  const inOrgContext = isWorkspaceContext && !!activeOrg
  const rootIsOwner = useSelector((state: any) => state.root.isOwner)
  const isOrgOwner = activeOrg?.role === 'owner'
  /** Only account/org owners can set My AI toggles after connect. */
  const canEditMyAi = inOrgContext ? isOrgOwner : !!rootIsOwner
  const rootAccountsRef = useRef<any[]>([])
  useEffect(() => {
    rootAccountsRef.current = rootAccounts || []
  }, [rootAccounts])

  // Always re-fetch linked accounts when this page mounts so the per-channel
  // account tables reflect connections made via OAuth redirect flows (e.g. GA,
  // GTM). Those flows clear `req.session.accounts` server-side via
  // `redirectBack`, but the page is remounted with whatever Redux had cached
  // from initial app login. Without this, freshly connected channels show as
  // "connected" (via `/integrations-keys`) but with an empty accounts table.
  useEffect(() => {
    if (isChannelAdvisorAccept) return
    retrieveAccounts().catch((err) => {
      console.warn('Failed to refresh accounts on Integrations mount:', err)
    })
  }, [isChannelAdvisorAccept, retrieveAccounts])

  /**
   *
   * Handle URL parameters for integration success and error messages
   *
   */
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search)

      // Handle ChannelAdvisor success and error messages
      const channeladvisorHandled = handleChannelAdvisorSuccess(params) || handleChannelAdvisorErrors(params)
      const tiktokHandled = handleTikTokSuccess() || handleTikTokErrors()
      const linkedinHandled = handleLinkedInSuccess() || handleLinkedInErrors()

      if (channeladvisorHandled || tiktokHandled || linkedinHandled) {
        // Remove the parameter from URL after a small delay to ensure toast shows
        setTimeout(() => {
          const newUrl = window.location.pathname
          window.history.replaceState({}, '', newUrl)
        }, 100)
      }
    } catch (error) {
      console.error('Error in URL parameter handling:', error)
    }
  }, [location.search])

  /**
   *
   * Fetch user and team accounts so we can get the ownerId
   *
   */
  const teamAccounts = useSelector(selectTeamAccounts)
  const teamAccountsStatus = useSelector(selectTeamAccountsStatus)
  useEffect(() => {
    if (teamAccountsStatus === 'idle') {
      dispatch(fetchTeamAccounts())
    }
  }, [teamAccountsStatus])

  // Wait for team accounts to load before calculating ownerId
  const ownerId = useMemo(() => {
    if (teamAccountsStatus === 'loading' || teamAccountsStatus === 'idle') {
      return null // Don't calculate ownerId until team accounts are loaded
    }
    return resolveIntegrationsOwnerId(user, teamAccounts)
  }, [user, teamAccounts, teamAccountsStatus])

  // Shared account IDs for PayHelm MCP AI only (owner + team members)
  const sharedAccountIds = useMemo(() => {
    if (!teamAccounts || !Array.isArray(teamAccounts)) return []
    return _.uniq(
      teamAccounts
        .map((t) => t.shared_with_id)
        .filter((id) => id != null && id !== '')
    )
  }, [teamAccounts])

  /**
   *
   * Handle integrations and categories
   *
   */
  const { data: integrations } = useIntegrations({
    accounts,
    ownerId,
    sharedAccountIds,
    currentUserId: user?.id,
    inOrgContext,
  })
  const { data: categories } = useCategories({ integrationIds: integrations.ids })

  const integrationEntities = integrations?.entities as
    | Record<string, { inkfrogOrderImportsRetentionNote?: string }>
    | undefined
  const inkfrogOrderImportsRetentionNote =
    integrationEntities?.inkfrog_listings?.inkfrogOrderImportsRetentionNote ?? ''

  const [selectedCategory, setSelectedCategory] = useState(categories.entities['all'])
  const [searchValue, setSearchValue] = useState('')
  const [limitModalType, setLimitModalType] = useState(null)
  const navigate = useNavigate()

  const onLimitExceeded = (type) => {
    setLimitModalType(type)
  }

  const gmcOauthToastShownRef = useRef(false)
  const googleOauthContaminationToastRef = useRef(false)
  const gmcOauthModalOpenedRef = useRef(false)
  const gmcIncompleteSetupCheckedRef = useRef(false)
  const subaccountOauthFetchStartedRef = useRef<string | null>(null)
  const [subaccountSetupModal, setSubaccountSetupModal] = useState<{
    platform: string
    connectionId: string
    subaccounts: SubaccountSetupEntry[]
  } | null>(null)
  const [subaccountSetupLoading, setSubaccountSetupLoading] = useState(false)
  const [subaccountSetupSaving, setSubaccountSetupSaving] = useState(false)
  // Google Marketing can grant AI-usable access to multiple products in one consent. Rather than
  // building a combined multi-section modal, we queue the same single-platform modal once per
  // granted product and show them back-to-back. `subaccountSetupFlow` tracks the full ordered
  // platform list + current index so the modal can show a multi-step progress indicator.
  const [subaccountSetupQueue, setSubaccountSetupQueue] = useState<Array<{ platform: string; connectionId: string }>>([])
  const [subaccountSetupFlow, setSubaccountSetupFlow] = useState<{
    platforms: string[]
    currentIndex: number
  } | null>(null)
  const subaccountSetupQueueRef = useRef(subaccountSetupQueue)
  const subaccountSetupFlowRef = useRef(subaccountSetupFlow)
  useEffect(() => {
    subaccountSetupQueueRef.current = subaccountSetupQueue
  }, [subaccountSetupQueue])
  useEffect(() => {
    subaccountSetupFlowRef.current = subaccountSetupFlow
  }, [subaccountSetupFlow])
  const allPublicKeys = useSelector(selectAllPublicKeys)
  const allPublicKeysStatus = useSelector(selectAllPublicKeysStatus)
  const mcpPublicKeysStatus = useSelector(selectMcpPublicKeysStatus)
  const connectionModalVisible = useSelector(selectConnectionModalVisible)
  const currentChannel = useSelector(selectCurrentChannel)

  const orgAwareRetrieveAccounts = useCallback(async () => {
    await retrieveAccounts()
  }, [retrieveAccounts])

  // Individual GA/GSC/GTM/GMC reconnects still use incremental auth. On the historically
  // Cloud-contaminated Marketing client that unions `bigquery.readonly`, papaya refuses the
  // grant and returns `*_oauth=scope_contaminated`. Combined Google Marketing is the reconnect
  // path that replaces that grant.
  useEffect(() => {
    if (isChannelAdvisorAccept) return
    const contaminatedParam = parseGoogleOAuthScopeContamination(location.search)
    if (!contaminatedParam) return

    const next = new URLSearchParams(location.search)
    for (const key of GOOGLE_OAUTH_STATUS_PARAMS) next.delete(key)
    next.delete('ga_connection_id')
    next.delete('gtm_connection_id')
    next.delete('gsc_connection_id')
    next.delete('google_marketing_connection_id')
    next.delete('saved')
    const qs = next.toString()
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true })

    if (googleOauthContaminationToastRef.current) return
    googleOauthContaminationToastRef.current = true
    toast.error(
      'This Google account still has Cloud/BigQuery scopes on the Marketing app. Reconnect via Google Marketing, not the individual product cards.',
      { autoClose: 12000, toastId: 'google-oauth-scope-contaminated' }
    )
  }, [isChannelAdvisorAccept, location.search, location.pathname, navigate])

  // GMC OAuth return: toast once, strip ?gmc_oauth via React Router, queue setup modal (unless user dismissed).
  useEffect(() => {
    if (isChannelAdvisorAccept) return
    const params = new URLSearchParams(location.search)
    if (params.get('gmc_oauth') !== 'success') return

    const next = new URLSearchParams(location.search)
    next.delete('gmc_oauth')
    const qs = next.toString()
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true })

    if (isGmcOAuthSetupDismissed()) return

    let toastAlreadyDone = false
    try {
      toastAlreadyDone = sessionStorage.getItem(GMC_OAUTH_TOAST_DONE_KEY) === '1'
    } catch {
      toastAlreadyDone = false
    }

    if (!toastAlreadyDone && !gmcOauthToastShownRef.current) {
      gmcOauthToastShownRef.current = true
      try {
        sessionStorage.setItem(GMC_OAUTH_TOAST_DONE_KEY, '1')
      } catch {
        /* ignore */
      }
      toast.success(
        'Google Merchant Center connected. Choose your Merchant Center account to finish setup.',
        { autoClose: 8000, toastId: 'gmc-oauth-success' }
      )
    }

    try {
      sessionStorage.setItem(GMC_OAUTH_SETUP_PENDING_KEY, '1')
    } catch {
      /* ignore */
    }
  }, [isChannelAdvisorAccept, location.search, location.pathname, navigate])

  // Fetches sub-accounts for one platform/connection and opens the setup modal for it. Shared by
  // the single-platform OAuth returns and by the Marketing queue (one call per granted product).
  const fetchAndOpenSubaccountSetup = useCallback((platform: string, connectionId: string) => {
    if (!canEditMyAi) {
      toast.info(
        'Connected successfully. Ask the account owner to enable AI access in Settings → My AI.',
        { toastId: 'my-ai-owner-only-setup', autoClose: 8000 }
      )
      return
    }
    setSubaccountSetupModal({ platform, connectionId, subaccounts: [] })
    setSubaccountSetupLoading(true)

    ;(async () => {
      try {
        const res = await fetch(`${baseUrl}/user/my-ai/refresh-subaccounts`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ platform, connection_id: connectionId }),
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error((err as any).error || 'Failed to load accounts')
        }
        const json = await res.json()
        const discovered: SubaccountSetupEntry[] = (json.subaccounts || []).map((s: any) => ({
          account_id: s.account_id,
          account_name: s.account_name,
          ai_enabled: s.ai_enabled,
        }))
        // Fall back to a single connection-level toggle when discovery finds nothing, so the
        // modal always lets the user enable/disable AI for the connection either way.
        const subaccounts = discovered.length
          ? discovered
          : [{ account_id: connectionId, account_name: null, ai_enabled: true }]
        setSubaccountSetupModal({ platform, connectionId, subaccounts })
        if (discovered.length === 0) {
          toast.info('Connected successfully. No sub-accounts found — you can still enable AI for this connection.')
        }
      } catch (err: any) {
        toast.error(err?.message || 'Failed to load AI sub-accounts')
        // Controlled Dialog: clearing `open` does not fire onOpenChange, so dismiss toast stays for X/Escape only.
        setSubaccountSetupModal(null)
        setSubaccountSetupFlow(null)
        setSubaccountSetupQueue([])
      } finally {
        setSubaccountSetupLoading(false)
      }
    })()
  }, [canEditMyAi])

  // Pops the next queued platform (Marketing's multi-product fan-out) or closes the modal. Reads
  // the queue via a ref (rather than a setState updater) so the side effects (fetch/close) happen
  // as plain imperative calls in this event handler, not inside a state updater function — updater
  // functions must be pure, and React 18 StrictMode double-invokes them in dev, which would have
  // double-fired the fetch (or skipped a queued platform) here.
  const advanceSubaccountSetupQueue = useCallback(() => {
    const queue = subaccountSetupQueueRef.current
    const flow = subaccountSetupFlowRef.current
    if (queue.length === 0) {
      // Programmatic close — Radix onOpenChange does not fire when `open` flips via props.
      setSubaccountSetupModal(null)
      setSubaccountSetupFlow(null)
      return
    }
    const [nextItem, ...rest] = queue
    setSubaccountSetupQueue(rest)
    if (flow) {
      setSubaccountSetupFlow({
        platforms: flow.platforms,
        currentIndex: Math.min(flow.currentIndex + 1, flow.platforms.length - 1),
      })
    }
    fetchAndOpenSubaccountSetup(nextItem.platform, nextItem.connectionId)
  }, [fetchAndOpenSubaccountSetup])

  // X / Escape / outside click only (wired via Dialog onOpenChange): abandon the entire queue.
  const dismissSubaccountSetup = useCallback(() => {
    setSubaccountSetupModal(null)
    setSubaccountSetupFlow(null)
    setSubaccountSetupQueue([])
    toast.info(
      'You can enable AI access for these accounts anytime in Settings → My AI.',
      { toastId: 'subaccount-setup-dismissed', autoClose: 8000 }
    )
  }, [])

  // GA / GTM / Google Ads / Google Search Console / BigQuery / Google Marketing OAuth return:
  // fetch sub-accounts and open the AI setup modal (queued sequentially for Marketing's
  // multiple granted products).
  useEffect(() => {
    if (isChannelAdvisorAccept) return

    const fromQuery = parseSubaccountOAuthReturn(location.search)
    // Prefer query params; fall back to cookie when the parent only refreshed after a popup close.
    if (fromQuery?.platform === 'google_ads') {
      setCookie('gads_oauth_setup', '', 0)
    }
    const resolved = fromQuery || consumeGoogleAdsOAuthSetupCookie()
    if (!resolved) return

    const fetchKey = `${resolved.platform}|${resolved.connectionId}`
    if (subaccountOauthFetchStartedRef.current === fetchKey) return
    subaccountOauthFetchStartedRef.current = fetchKey

    const next = new URLSearchParams(location.search)
    next.delete('ga_oauth')
    next.delete('ga_connection_id')
    next.delete('gtm_oauth')
    next.delete('gtm_connection_id')
    next.delete('gads_oauth')
    next.delete('gads_connection_id')
    next.delete('gsc_oauth')
    next.delete('gsc_connection_id')
    next.delete('bigquery_oauth')
    next.delete('bigquery_connection_id')
    next.delete('google_marketing_oauth')
    next.delete('google_marketing_connection_id')
    next.delete('saved')
    const qs = next.toString()
    if (location.search) {
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true })
    }

    if (resolved.platform === 'google_marketing') {
      const queue = (fromQuery?.saved || [])
        .filter((p) => MARKETING_SUBACCOUNT_PLATFORMS.has(p))
        .map((platform) => ({ platform, connectionId: resolved.connectionId }))
      if (queue.length === 0) return
      const [first, ...rest] = queue
      setSubaccountSetupFlow({
        platforms: queue.map((item) => item.platform),
        currentIndex: 0,
      })
      setSubaccountSetupQueue(rest)
      fetchAndOpenSubaccountSetup(first.platform, first.connectionId)
      return
    }

    setSubaccountSetupFlow({ platforms: [resolved.platform], currentIndex: 0 })
    setSubaccountSetupQueue([])
    fetchAndOpenSubaccountSetup(resolved.platform, resolved.connectionId)
  }, [isChannelAdvisorAccept, location.search, location.pathname, navigate, fetchAndOpenSubaccountSetup])

  const saveSubaccountSetup = useCallback(
    async (selections: SubaccountSetupEntry[]) => {
      if (!subaccountSetupModal) return
      setSubaccountSetupSaving(true)
      try {
        const puts: Promise<Response>[] = selections.map((sub) =>
          fetch(`${baseUrl}/user/my-ai`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              platform: subaccountSetupModal.platform,
              account_id: sub.account_id,
              ai_enabled: sub.ai_enabled,
            }),
          })
        )
        if (selections.some((s) => s.ai_enabled) && !selections.some((s) => s.account_id === subaccountSetupModal.connectionId)) {
          puts.push(
            fetch(`${baseUrl}/user/my-ai`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                platform: subaccountSetupModal.platform,
                account_id: subaccountSetupModal.connectionId,
                ai_enabled: true,
              }),
            })
          )
        }
        const results = await Promise.all(puts)
        if (results.some((r) => !r.ok)) throw new Error('Failed to save')
        toast.success('AI access settings saved')
        advanceSubaccountSetupQueue()
      } catch {
        toast.error('Failed to save AI access settings')
      } finally {
        setSubaccountSetupSaving(false)
      }
    },
    [subaccountSetupModal, advanceSubaccountSetupQueue]
  )

  // Open setup modal once when integration data is ready (do not reopen after Close).
  useEffect(() => {
    if (isChannelAdvisorAccept || gmcOauthModalOpenedRef.current || isGmcOAuthSetupDismissed()) return
    let pending = false
    try {
      pending = sessionStorage.getItem(GMC_OAUTH_SETUP_PENDING_KEY) === '1'
    } catch {
      pending = false
    }
    if (!pending) return

    const gmcIntegration =
      integrations?.entities && integrations.entities['google_merchant_center']
    if (!gmcIntegration) return

    gmcOauthModalOpenedRef.current = true
    try {
      sessionStorage.removeItem(GMC_OAUTH_SETUP_PENDING_KEY)
    } catch {
      /* ignore */
    }
    dispatch(setCurrentChannel(gmcIntegration))
    dispatch(openConnectionModal())
  }, [isChannelAdvisorAccept, integrations?.entities, dispatch])

  // Prompt existing GMC connections missing an account id (multi-account picker or manual fallback).
  useEffect(() => {
    if (isChannelAdvisorAccept || gmcIncompleteSetupCheckedRef.current) return
    if (!ownerId || allPublicKeysStatus !== 'succeeded' || connectionModalVisible) return
    if (isGmcOAuthSetupDismissed()) return

    let oauthPending = false
    try {
      oauthPending = sessionStorage.getItem(GMC_OAUTH_SETUP_PENDING_KEY) === '1'
    } catch {
      oauthPending = false
    }
    if (oauthPending) return

    const gmcIntegration =
      integrations?.entities && integrations.entities['google_merchant_center']
    if (!gmcIntegration?.connected) return

    const incompleteRows = collectGmcRows(gmcIntegration, allPublicKeys).filter(
      (row) => row.connectionId && !row.merchantCenterAccountId && !isGmcAccountSetupDismissed(row.connectionId)
    )
    if (!incompleteRows.length) {
      gmcIncompleteSetupCheckedRef.current = true
      return
    }

    gmcIncompleteSetupCheckedRef.current = true

    let active = true

    ;(async () => {
      let needsModal = false

      for (const row of incompleteRows) {
        try {
          const { accounts, error } = await fetchGmcAccountsForConnection(row.connectionId)
          if (!active) return

          if (accounts.length === 1) {
            await saveMerchantCenterAccountId(row.connectionId, accounts[0].id)
            if (!active) return
            continue
          }

          if (accounts.length > 1 || error || accounts.length === 0) {
            needsModal = true
            break
          }
        } catch {
          needsModal = true
          break
        }
      }

      if (!active) return

      await retrieveAccounts()
      if (!active) return

      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
      }

      if (needsModal && active) {
        toast.info('Select your Merchant Center account to finish setup.', {
          autoClose: 8000,
          toastId: 'gmc-account-setup',
        })
        dispatch(setCurrentChannel(gmcIntegration))
        dispatch(openConnectionModal())
      }
    })().catch((err) => {
      console.warn('Failed to resolve incomplete GMC account setup:', err)
    })

    return () => {
      active = false
    }
  }, [
    allPublicKeys,
    allPublicKeysStatus,
    connectionModalVisible,
    dispatch,
    integrations?.entities,
    isChannelAdvisorAccept,
    ownerId,
    retrieveAccounts,
  ])

  const integrationCategoryLabels = useMemo(() => {
    const map: Record<string, string[]> = {}
    categories.ids.forEach((catId) => {
      if (catId === 'all' || catId === 'most_popular') return
      const cat = categories.entities[catId]
      cat?.integrationIds?.forEach((id) => {
        if (!map[id]) map[id] = []
        map[id].push(String(cat.title).toLowerCase())
      })
    })
    return map
  }, [categories])

  const searchFilterPredicate = (integration: Record<string, unknown> = {}) => {
    if (_.isEmpty(searchValue)) return true
    const lower = String(searchValue).toLowerCase().trim()
    if (!lower) return true
    const title = integration.title as string | undefined
    if (title && title.toLowerCase().includes(lower)) return true
    const description = integration.description as string | undefined
    if (description && String(description).toLowerCase().includes(lower)) return true
    const channel = integration.channel as string | undefined
    if (channel && String(channel).toLowerCase().includes(lower)) return true
    const id = integration.id as string | undefined
    if (id) {
      const idLc = id.toLowerCase()
      if (idLc.includes(lower) || idLc.replace(/_/g, ' ').includes(lower)) return true
    }
    return (integrationCategoryLabels[id || ''] || []).some((lbl) => lbl.includes(lower))
  }

  const payhelmApiKeyMatchesSearch = searchFilterPredicate(payhelmApiKeyCardSearchRecord)
  const payhelmMcpCustomToolsMatchesSearch = searchFilterPredicate(payhelmMcpCustomToolsCardSearchRecord)
  const alexaSkillMatchesSearch = searchFilterPredicate(alexaSkillCardSearchRecord)

  const selectedCategoryIntegrations =
    selectedCategory.integrationIds &&
    selectedCategory.integrationIds.map((_id) => integrations.entities[_id]).filter((v) => v)
  const connectedIntegrations =
    selectedCategoryIntegrations &&
    selectedCategoryIntegrations.filter(({ connected }) => connected).filter(searchFilterPredicate)
  const unconnectedIntegrations =
    selectedCategoryIntegrations &&
    selectedCategoryIntegrations.filter(({ connected }) => !connected).filter(searchFilterPredicate)
  const hasGeneratedApiKey = Boolean(user?.api_key || user?.api_key_create_time)
  const hasCustomTools = (customTools?.length ?? 0) > 0
  const showPayhelmApiKeyConnected = hasGeneratedApiKey && payhelmApiKeyMatchesSearch
  const showPayhelmApiKeyRecommended = !hasGeneratedApiKey && payhelmApiKeyMatchesSearch
  const showMcpCustomToolsConnected = hasCustomTools && payhelmMcpCustomToolsMatchesSearch
  const showMcpCustomToolsRecommended = !hasCustomTools && payhelmMcpCustomToolsMatchesSearch
  const hasConnectedContent =
    (connectedIntegrations && connectedIntegrations.length > 0) ||
    showPayhelmApiKeyConnected ||
    showMcpCustomToolsConnected
  // Alexa card always appears in "Recommended" (it has no connected state in the integration
  // list — connection status is shown inside the card itself via /alexa/status).
  const hasRecommendedContent =
    (unconnectedIntegrations && unconnectedIntegrations.length > 0) ||
    showPayhelmApiKeyRecommended ||
    showMcpCustomToolsRecommended ||
    alexaSkillMatchesSearch

  const searchQueryTrimmed = String(searchValue).trim()
  const hasActiveSearch = searchQueryTrimmed.length > 0
  const noSearchResults =
    hasActiveSearch && !hasConnectedContent && !hasRecommendedContent

  const mcpAiIntegration = integrations?.entities?.payhelm_ai_mcp
  const showOrgMcpKeyBanner =
    inOrgContext &&
    !!mcpAiIntegration &&
    !mcpAiIntegration.connected &&
    mcpPublicKeysStatus === 'succeeded'

  const openPayHelmMcpAiSetup = () => {
    if (!mcpAiIntegration) return
    dispatch(setCurrentChannel(mcpAiIntegration))
    dispatch(openConnectionModal())
  }

  /**
   *
   * Handle modals
   *
   */
  const backfillModalVisible = useSelector(selectBackillModalVisible)
  const settingsModalVisible = useSelector(selectSettingsModalVisible)
  const csvModalVisible = useSelector(selectCsvModalVisible)
  const inkfrogCsvModalVisible = useSelector(selectInkfrogCsvModalVisible)
  const importOrdersModalVisible = useSelector(selectImportOrdersModalVisible)
  const inkfrogOrdersCsvModalVisible = useSelector(selectInkfrogOrdersCsvModalVisible)

  return (
    <>
      <div className="tw-w-full tw-min-h-screen tw-p-8">
        <div className="tw-flex tw-flex-col tw-gap-4 tw-mb-6">
          <Search searchValue={searchValue} setSearchValue={setSearchValue} />
          <CategoryPills
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
          />
        </div>

          {showOrgMcpKeyBanner && (
            <div className="tw-mb-6 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3">
              <div className="tw-text-sm tw-text-amber-950">
                <span className="tw-font-semibold">Generate a PayHelm MCP AI key for this organization.</span>{' '}
                Org Slack channels and Agent Mode need this org’s own key — a personal MCP key will not work here.
              </div>
              <Button variant="warning" size="sm" className="tw-shrink-0" onClick={openPayHelmMcpAiSetup}>
                Generate MCP key
              </Button>
            </div>
          )}

          {hasConnectedContent && (
            <div className="tw-mb-10">
              <h5 className="tw-mb-4 tw-font-semibold">Your connected integrations</h5>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-1 lg:tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-6">
                {showPayhelmApiKeyConnected && <PayhelmAPIKeyCard />}
                {showMcpCustomToolsConnected && <PayHelmMcpCustomToolsCard />}
                {connectedIntegrations?.map((_integration) => {
                  return (
                    <IntegrationCard
                      key={_integration.id}
                      integration={_integration}
                      ownerId={ownerId}
                      sharedAccountIds={sharedAccountIds}
                      csvModalVisible={csvModalVisible}
                      onLimitExceeded={onLimitExceeded}
                      retrieveAccounts={inOrgContext ? orgAwareRetrieveAccounts : retrieveAccounts}
                      canDisconnectAllTeamIntegrations={canDisconnectAllTeamIntegrations}
                      currentUserEmail={user?.email ?? ''}
                    />
                  )
                })}
              </div>
            </div>
          )}

          {hasRecommendedContent && (
            <div>
              <h5 className="tw-mb-4 tw-font-semibold">Recommended integrations</h5>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6">
                {showPayhelmApiKeyRecommended && <PayhelmAPIKeyCard />}
                {showMcpCustomToolsRecommended && <PayHelmMcpCustomToolsCard />}
                {alexaSkillMatchesSearch && <AlexaSkillCard />}
                {unconnectedIntegrations?.map((_integration) => {
                  return (
                    <IntegrationCard
                      key={_integration.id}
                      integration={_integration}
                      ownerId={ownerId}
                      sharedAccountIds={sharedAccountIds}
                      csvModalVisible={csvModalVisible}
                      onLimitExceeded={onLimitExceeded}
                      retrieveAccounts={inOrgContext ? orgAwareRetrieveAccounts : retrieveAccounts}
                    />
                  )
                })}
              </div>
            </div>
          )}

          {noSearchResults && (
            <div
              className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-6 tw-py-14 tw-text-center"
              role="status"
            >
              <p className="tw-text-slate-700 tw-text-base tw-font-medium tw-mb-2">No matching integrations</p>
              <p className="tw-text-slate-500 tw-text-sm tw-mb-5 tw-max-w-md tw-mx-auto tw-break-words">
                Nothing matches{' '}
                <span className="tw-font-mono tw-text-slate-700">"{searchQueryTrimmed}"</span>
                {selectedCategory?.id && selectedCategory.id !== 'all' && selectedCategory.id !== 'most_popular'
                  ? <> in {String(selectedCategory.title)}</>
                  : null}
                . Try different keywords or pick another category.
              </p>
              <button
                type="button"
                className="tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-text-blue-700 tw-underline tw-underline-offset-2"
                onClick={() => setSearchValue('')}
              >
                Clear search
              </button>
            </div>
          )}
      </div>

      <ConnectionModal
        ownerId={ownerId}
        sharedAccountIds={sharedAccountIds}
        visible={connectionModalVisible}
        closeModal={() => {
          if (currentChannel?.id === 'google_merchant_center') {
            dismissGmcOAuthSetupModal()
          }
          dispatch(closeConnectionModal())
        }}
        accounts={accounts}
        retrieveAccounts={inOrgContext ? orgAwareRetrieveAccounts : retrieveAccounts}
      />

      <BackfillModal visible={backfillModalVisible} closeModal={() => dispatch(closeBackfillModal())} />
      <SettingsModal
        visible={settingsModalVisible}
        accounts={accounts}
        closeModal={() => dispatch(closeSettingsModal())}
        retrieveAccounts={retrieveAccounts}
      />
      <UploadModal open={csvModalVisible} closeModal={() => dispatch(closeCsvModal())} />
      <InkfrogListingsUploadModal
        open={inkfrogCsvModalVisible}
        closeModal={() => dispatch(closeInkfrogCsvModal())}
      />
      <ImportOrdersModal
        visible={importOrdersModalVisible}
        closeModal={() => dispatch(closeImportOrdersModal())}
      />
      <InkfrogOrdersUploadModal
        open={inkfrogOrdersCsvModalVisible}
        closeModal={() => dispatch(closeInkfrogOrdersCsvModal())}
        orderImportsRetentionNote={inkfrogOrderImportsRetentionNote}
      />
      <CustomToolsIntegrationsModal
        show={customToolsModalVisible}
        onHide={() => dispatch(closeCustomToolsModal())}
      />

      <AiSubaccountSetupModal
        show={!!subaccountSetupModal}
        loading={subaccountSetupLoading}
        platform={subaccountSetupModal?.platform || ''}
        subaccounts={subaccountSetupModal?.subaccounts || []}
        onClose={dismissSubaccountSetup}
        onSkip={advanceSubaccountSetupQueue}
        onSave={saveSubaccountSetup}
        saving={subaccountSetupSaving}
        stepPlatforms={subaccountSetupFlow?.platforms}
        stepIndex={subaccountSetupFlow?.currentIndex ?? 0}
      />

      <Modal show={!!limitModalType} onHide={() => setLimitModalType(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upgrade Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {limitModalType === 'store'
            ? 'You have reached the maximum number of store integrations for your current plan. Please upgrade to connect more stores.'
            : 'You have reached the maximum number of integrations for your current plan. Please upgrade to connect more integrations.'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLimitModalType(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setLimitModalType(null)
              dispatch(changeApplication('plan'))
              navigate('/plan')
            }}
          >
            Upgrade Plan
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default Integrations
